export interface ElementRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PageContext {
  url: string;
  route: string;
  title?: string;
}

export interface ElementContext {
  selector: string;
  html: string;
  rect?: ElementRect;
}

export interface EnvironmentContext {
  viewport: { width: number; height: number };
  userAgent: string;
  devicePixelRatio?: number;
}

export interface CapturedContext {
  page: PageContext;
  element: ElementContext;
  environment: EnvironmentContext;
}

const MAX_HTML_LENGTH = 4000;
const SCREENSHOT_TIMEOUT_MS = 6000;
/** Above this data-URL size, retry as JPEG; server rejects > 2 MB. */
const PNG_SIZE_LIMIT = 1_500_000;
const JPEG_SIZE_LIMIT = 2_000_000;

export function captureContext(el: Element, selector: string): CapturedContext {
  const rect = el.getBoundingClientRect();
  const title = document.title;

  return {
    page: {
      url: location.href,
      route: location.pathname + location.search,
      ...(title ? { title } : {}),
    },
    element: {
      selector,
      html: trimHtml(el.outerHTML),
      rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
    },
    environment: {
      viewport: { width: window.innerWidth, height: window.innerHeight },
      userAgent: navigator.userAgent,
      devicePixelRatio: window.devicePixelRatio || 1,
    },
  };
}

function trimHtml(html: string): string {
  return html.replace(/\s+/g, ' ').trim().slice(0, MAX_HTML_LENGTH);
}

function withTimeout<T>(promise: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('screenshot timeout')), SCREENSHOT_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== null) clearTimeout(timer);
  });
}

/**
 * Element screenshot via lazily imported html-to-image. Never throws and never
 * blocks submission: any failure (timeout, tainted canvas, oversized output)
 * degrades to null.
 */
export async function captureScreenshot(el: Element): Promise<string | null> {
  if (!(el instanceof HTMLElement)) return null;
  try {
    const { toPng, toJpeg } = await import('html-to-image');
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const png = await withTimeout(toPng(el, { pixelRatio, skipFonts: true }));
    if (png.length <= PNG_SIZE_LIMIT) return png;
    const jpeg = await withTimeout(
      toJpeg(el, { pixelRatio: 1, quality: 0.8, skipFonts: true, backgroundColor: '#ffffff' }),
    );
    return jpeg.length <= JPEG_SIZE_LIMIT ? jpeg : null;
  } catch {
    return null;
  }
}
